<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert
            v-if="error"
            type="error"
            class="mb-4"
            closable
            @click:close="error = ''"
          >
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else class="obra-form-panel">
            <div class="obra-form-header">
              <v-icon size="22" color="#ffffff">mdi-checkbox-marked-outline</v-icon>
              <span>{{ editing ? 'EDITAR OBRA' : 'INGRESA TU OBRA' }}</span>
            </div>

            <div class="obra-form-body">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="playForm.title" label="Título" variant="outlined" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="playForm.genre"
                    :items="genres"
                    item-title="name"
                    item-value="id"
                    label="Género"
                    variant="outlined"
                  />
                </v-col>
                <v-col v-if="isAdmin" cols="12" md="6">
                  <v-select
                    v-model="playForm.company"
                    :items="companies"
                    item-title="name"
                    item-value="id"
                    label="Compañía"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="playForm.productora"
                    :items="productoras"
                    item-title="name"
                    item-value="id"
                    label="Productora"
                    variant="outlined"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="playForm.image"
                    label="URL imagen"
                    variant="outlined"
                    hint="Pega una URL o sube un archivo (JPG/PNG)"
                    persistent-hint
                  />
                  <div class="obra-upload-row">
                    <input
                      ref="imageInput"
                      type="file"
                      accept="image/*"
                      class="obra-file-hidden"
                      @change="onImageSelected"
                    />
                    <button type="button" class="obra-upload-btn" @click="triggerImagePicker">
                      <v-icon size="16">mdi-image-outline</v-icon>
                      SUBIR IMAGEN
                    </button>
                    <span v-if="imageFile" class="obra-file-name">{{ imageFile.name }}</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="playForm.video_url" label="URL video (YouTube)" variant="outlined" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model.number="playForm.duration_minutes" label="Duración (min)" type="number" variant="outlined" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model="playForm.audience" label="Público" variant="outlined" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model="playForm.start_date" label="Inicio temporada" type="date" variant="outlined" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model="playForm.end_date" label="Término temporada" type="date" variant="outlined" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model.number="playForm.price" label="Precio (CLP)" type="number" variant="outlined" />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model.number="playForm.price_region" label="Precio región (CLP)" type="number" variant="outlined" />
                </v-col>
                <v-col cols="12">
                  <v-textarea v-model="playForm.description" label="Reseña" rows="3" variant="outlined" />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="playForm.stage"
                    label="Requerimientos de escenario"
                    rows="3"
                    auto-grow
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="playForm.directors" label="Directores" variant="outlined" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="playForm.actors"
                    label="Actores"
                    variant="outlined"
                    hint="Separa los nombres de los actores con una coma. Ej.: Juan Pérez, Ana Soto, Diego Rojas"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="playForm.tags"
                    :items="tags"
                    item-title="name"
                    item-value="id"
                    label="Tags"
                    multiple
                    chips
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch v-model="playForm.active" label="Activa" color="primary" />
                </v-col>
              </v-row>

              <div class="obra-form-actions">
                <button type="button" class="obra-cancel-btn" @click="cancel">
                  CANCELAR Y VOLVER
                </button>
                <button type="button" class="obra-save-btn" :disabled="saving" @click="savePlay">
                  {{ saving ? 'GUARDANDO…' : 'GUARDAR' }}
                </button>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import {
  fetchPlayAdmin,
  createPlay,
  updatePlay,
  uploadPlayImage,
  fetchGenres,
  fetchTags,
  fetchCompanies,
  fetchProductoras,
} from "@/services/teatroApi";

const emptyPlay = {
  title: "",
  description: "",
  synopsis: "",
  image: "",
  video_url: "",
  genre: null,
  company: null,
  productora: null,
  duration_minutes: 45,
  audience: "Familiar",
  format: "in_person",
  cast_size: 1,
  stage: "",
  directors: "",
  actors: "",
  price: 0,
  price_region: 0,
  start_date: "",
  end_date: "",
  tags: [],
  active: true,
  order: 0,
};

export default {
  name: "PlayForm",
  props: {
    // Passed from the Django route when editing (e.g. <play-form :play-id="3"/>).
    playId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      error: "",
      genres: [],
      tags: [],
      companies: [],
      productoras: [],
      imageFile: null,
      playForm: { ...emptyPlay },
      formatOptions: [
        { value: "in_person", label: "Presencial" },
        { value: "online", label: "Online" },
        { value: "hybrid", label: "Híbrido" },
      ],
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.is_admin;
    },
    editing() {
      return this.playId !== null && this.playId !== "" && this.playId !== undefined;
    },
  },
  async created() {
    await this.loadAuth();
    await this.load();
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const [genres, tags, companies, productoras] = await Promise.all([
          fetchGenres(),
          fetchTags(),
          fetchCompanies(),
          fetchProductoras().catch(() => []),
        ]);
        this.genres = genres || [];
        this.tags = tags || [];
        this.companies = companies || [];
        this.productoras = productoras || [];

        if (this.editing) {
          const play = await fetchPlayAdmin(this.playId);
          this.playForm = {
            title: play.title || "",
            description: play.description || "",
            synopsis: play.synopsis || "",
            image: play.image || "",
            video_url: play.video_url || "",
            genre: play.genre?.id || null,
            company: play.company?.id || null,
            productora: play.productora?.id || null,
            duration_minutes: play.duration_minutes || 45,
            audience: play.audience || "Familiar",
            format: play.format || "in_person",
            cast_size: play.cast_size || 1,
            stage: play.stage || "",
            directors: play.directors || "",
            actors: play.actors || "",
            price: play.price || 0,
            price_region: play.price_region || 0,
            start_date: play.start_date || "",
            end_date: play.end_date || "",
            tags: (play.tags || []).map((t) => t.id),
            active: play.active !== false,
            order: play.order || 0,
          };
        } else {
          this.playForm = {
            ...emptyPlay,
            company: this.companies.length === 1 ? this.companies[0].id : null,
          };
        }
      } catch (e) {
        this.error = "No se pudieron cargar los datos.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    triggerImagePicker() {
      this.$refs.imageInput?.click();
    },
    onImageSelected(e) {
      const file = e.target.files && e.target.files[0];
      this.imageFile = file || null;
    },
    async savePlay() {
      this.saving = true;
      this.error = "";
      try {
        const payload = {
          ...this.playForm,
          price: Number(this.playForm.price) || 0,
          price_region: Number(this.playForm.price_region) || 0,
          duration_minutes: Number(this.playForm.duration_minutes) || 0,
          cast_size: Number(this.playForm.cast_size) || 0,
          order: Number(this.playForm.order) || 0,
          tags: this.playForm.tags || [],
        };
        // Save the play first so we have an id (and company) for the S3 path.
        const saved = this.editing
          ? await updatePlay(this.playId, payload)
          : await createPlay(payload);

        // If a file was chosen, upload it to S3 and store its URL on the play.
        const savedId = (saved && saved.id) || this.playId;
        if (this.imageFile && savedId) {
          await uploadPlayImage(savedId, this.imageFile);
        }
        window.location.href = "/admin-obras/";
      } catch (e) {
        // Parse validation errors from backend
        const responseData = e.response?.data;
        if (responseData && typeof responseData === 'object') {
          const errorMessages = [];
          
          // Handle field-specific errors
          for (const [field, errors] of Object.entries(responseData)) {
            if (Array.isArray(errors)) {
              const fieldLabel = this.getFieldLabel(field);
              errors.forEach(err => {
                errorMessages.push(`${fieldLabel}: ${err}`);
              });
            } else if (typeof errors === 'string') {
              errorMessages.push(errors);
            }
          }
          
          // Handle non_field_errors
          if (responseData.non_field_errors && Array.isArray(responseData.non_field_errors)) {
            responseData.non_field_errors.forEach(err => {
              errorMessages.push(err);
            });
          }
          
          this.error = errorMessages.length > 0 
            ? errorMessages.join('\n') 
            : (e.response?.data?.detail || "No se pudo guardar la obra.");
        } else {
          this.error = e.response?.data?.detail || "No se pudo guardar la obra.";
        }
        console.error(e);
        this.saving = false;
      }
    },
    getFieldLabel(field) {
      const labels = {
        title: 'Título',
        description: 'Reseña',
        genre: 'Género',
        company: 'Compañía',
        duration_minutes: 'Duración',
        audience: 'Público',
        format: 'Formato',
        cast_size: 'N° actores',
        stage: 'Requerimientos de escenario',
        directors: 'Directores',
        actors: 'Actores',
        price: 'Precio',
        price_region: 'Precio región',
        start_date: 'Inicio temporada',
        end_date: 'Término temporada',
        tags: 'Tags',
      };
      return labels[field] || field;
    },
    cancel() {
      window.location.href = "/admin-obras/";
    },
  },
};
</script>

<style scoped>
.obra-form-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.obra-form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.obra-form-body {
  padding: 30px 34px 28px;
}

.obra-form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 26px;
  margin-top: 18px;
}

.obra-cancel-btn {
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  cursor: pointer;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  transition: opacity 0.2s ease;
}

.obra-cancel-btn:hover {
  opacity: 0.72;
}

.obra-save-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 34px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.obra-save-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.45);
}

.obra-save-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
  box-shadow: none;
}

/* Force the Vuetify fields to the white look of the mockup. */
.obra-form-panel :deep(.v-field) {
  background: #ffffff !important;
  border-radius: 8px !important;
}

.obra-form-panel :deep(.v-field__outline) {
  --v-field-border-opacity: 0;
  color: transparent !important;
}

.obra-form-panel :deep(.v-field__input),
.obra-form-panel :deep(.v-field input),
.obra-form-panel :deep(.v-field textarea),
.obra-form-panel :deep(.v-select__selection-text) {
  color: #1a1a1a !important;
}

.obra-form-panel :deep(.v-field-label) {
  color: #7a7a7a !important;
  opacity: 1;
}

/* Texto de ayuda (hint) debajo del campo, legible sobre el panel oscuro. */
.obra-form-panel :deep(.v-messages),
.obra-form-panel :deep(.v-messages__message) {
  color: rgba(255, 255, 255, 0.75) !important;
  opacity: 1;
}

/* Al "subir", el label queda a caballo entre el campo blanco y el panel oscuro,
   por lo que ningún color plano se ve bien. Le damos un fondo oscuro tipo
   etiqueta con texto blanco para que siempre contraste. */
.obra-form-panel :deep(.v-field-label.v-field-label--floating) {
  color: #ffffff !important;
  background: #17100f;
  padding: 1px 6px;
  border-radius: 4px;
}

.obra-form-panel :deep(.v-field__append-inner .v-icon),
.obra-form-panel :deep(.v-field__clearable .v-icon) {
  color: #7a7a7a !important;
}

.obra-form-panel :deep(.v-chip) {
  background: #eceff1 !important;
  color: #1a1a1a !important;
}

.obra-form-panel :deep(.v-switch .v-label) {
  color: var(--color-theater-text) !important;
  opacity: 1;
}

/* Image upload control. */
.obra-upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}

.obra-file-hidden {
  display: none;
}

.obra-upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.obra-upload-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.4);
}

.obra-file-name {
  font-size: 12px;
  color: var(--color-theater-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 55%;
}
</style>
